import React from 'react';
import { X } from 'lucide-react';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';

interface PrivacyPolicySheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export const PrivacyPolicySheet: React.FC<PrivacyPolicySheetProps> = ({ isOpen, onOpenChange }) => {
  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="h-[96vh] flex flex-col p-0 rounded-t-2xl border-t border-border [&>button]:hidden"
      >
        {/* Header */}
        <div className="flex-shrink-0 px-4 pt-4 pb-4 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="w-10" />
            <h2 className="text-lg font-semibold text-foreground">Privacy Policy</h2>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onOpenChange(false)}
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-4 py-5 space-y-5 text-sm text-muted-foreground">
          <p className="text-xs text-muted-foreground/60">Last Updated: March 25, 2026</p>

          <p>
            Arab Western Ltd ("we", "our", or "us") respects your privacy and is committed to protecting your personal
            data. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you
            visit our website (https://forkit.uk) or use the Fork It mobile application (the "App").
          </p>
          <p>
            Please read this Privacy Policy carefully. If you do not agree with the terms of this Privacy Policy, please
            do not access or use the App.
          </p>

          <section className="space-y-3">
            <h3 className="text-base font-semibold text-foreground">1. Information We Collect</h3>
            <p>
              We collect information that you voluntarily provide to us, as well as data automatically collected when
              you use the App.
            </p>

            <p className="font-medium text-foreground">A. Personal Data You Provide</p>
            <ul className="space-y-2 list-disc list-inside">
              <li>
                <span className="font-medium text-foreground">Account Information:</span> When you register, we collect
                your email address, username, password, and profile picture (avatar).
              </li>
              <li>
                <span className="font-medium text-foreground">User-Generated Content:</span> Any data you upload to the
                platform, including photos ("Snaps"), videos ("Drools"), comments, dish submissions, and ratings.
              </li>
              <li>
                <span className="font-medium text-foreground">Communications:</span> Information you provide when
                contacting us for support.
              </li>
            </ul>

            <p className="font-medium text-foreground">B. Data Collected Automatically</p>
            <ul className="space-y-2 list-disc list-inside">
              <li>
                <span className="font-medium text-foreground">Device &amp; Usage Data:</span> IP address, operating
                system, device model, app version, unique device identifiers, and data regarding your interaction with
                the App (e.g., which dishes you upvote, save, or share).
              </li>
              <li>
                <span className="font-medium text-foreground">Location Data:</span> We request access to your device's
                location to show you nearby dishes and restaurants. We only access this data while you are actively
                using the App, subject to the permissions you grant in your device settings. If you disable location
                services, you can still manually search for locations.
              </li>
              <li>
                <span className="font-medium text-foreground">Camera and Photo Library Access:</span> We request access
                to your device's camera and photo library strictly to allow you to capture and upload photos and videos
                of food. We only access images or videos that you explicitly select for upload.
              </li>
            </ul>
          </section>

          <section className="space-y-2">
            <h3 className="text-base font-semibold text-foreground">2. How We Use Your Information</h3>
            <p>We use the information we collect to:</p>
            <ul className="space-y-1 list-disc list-inside">
              <li>Create and manage your user account.</li>
              <li>
                Provide, operate, and maintain the core features of the App (e.g., displaying nearby food, saving your
                favorites, ranking dishes).
              </li>
              <li>Facilitate user-to-user interactions (e.g., comments, upvotes).</li>
              <li>Improve App performance and user experience by analyzing usage trends.</li>
              <li>Identify, debug, and fix crashes or technical issues.</li>
              <li>Prevent fraud, unauthorized access, and enforce our Terms of Service.</li>
            </ul>
          </section>

          <section className="space-y-2">
            <h3 className="text-base font-semibold text-foreground">3. Third-Party Service Providers</h3>
            <p>
              We do not sell your personal data. However, we share your data with trusted third-party service providers
              ("Processors") who assist us in operating the App. These providers are contractually obligated to protect
              your data:
            </p>
            <ul className="space-y-1 list-disc list-inside">
              <li>
                <span className="font-medium text-foreground">Supabase:</span> Provides our backend database, user
                authentication, and media storage (for your uploaded Snaps and Drools).
              </li>
              <li>
                <span className="font-medium text-foreground">Vercel:</span> Hosts our web application infrastructure.
              </li>
              <li>
                <span className="font-medium text-foreground">Google (Places API &amp; Google Analytics 4):</span>{' '}
                Provides location search functionality and helps us understand overall App usage patterns.
              </li>
            </ul>
          </section>

          <section className="space-y-2">
            <h3 className="text-base font-semibold text-foreground">4. Data Retention and Deletion</h3>
            <p>
              We retain your personal data only for as long as your account is active or as necessary to provide you
              with the App's services.
            </p>
            <p className="font-medium text-foreground">How to Delete Your Account and Data:</p>
            <p>
              You have the right to request the deletion of your account and all associated personal data at any time.
            </p>
            <ul className="space-y-1 list-disc list-inside">
              <li>
                <span className="font-medium text-foreground">In-App:</span> Open the Fork It App, navigate to Profile
                &gt; Account Settings, and select Delete Account.
              </li>
              <li>
                <span className="font-medium text-foreground">Via Email:</span> You may also request data deletion by
                contacting us at mediaforkit@gmail.com.
              </li>
            </ul>
            <p>
              Upon your request, we will delete your account credentials, profile information, and user-generated
              content (or anonymize it where legally permissible or technically required to maintain the integrity of
              the public dish rankings).
            </p>
          </section>

          <section className="space-y-2">
            <h3 className="text-base font-semibold text-foreground">5. Your Data Protection Rights (UK GDPR)</h3>
            <p>
              If you are a resident of the United Kingdom or the European Economic Area (EEA), you have the following
              rights under the UK General Data Protection Regulation (UK GDPR):
            </p>
            <ul className="space-y-1 list-disc list-inside">
              <li>
                <span className="font-medium text-foreground">The right to access:</span> You can request copies of
                your personal data.
              </li>
              <li>
                <span className="font-medium text-foreground">The right to rectification:</span> You can request that
                we correct inaccurate information or complete incomplete information.
              </li>
              <li>
                <span className="font-medium text-foreground">The right to erasure:</span> You can request that we
                erase your personal data, under certain conditions.
              </li>
              <li>
                <span className="font-medium text-foreground">The right to restrict processing:</span> You can request
                that we restrict the processing of your personal data, under certain conditions.
              </li>
              <li>
                <span className="font-medium text-foreground">The right to data portability:</span> You can request
                that we transfer the data we have collected to another organization, or directly to you.
              </li>
            </ul>
            <p>
              To exercise any of these rights, please contact us at mediaforkit@gmail.com. We have one month to
              respond to your request.
            </p>
          </section>

          <section className="space-y-2">
            <h3 className="text-base font-semibold text-foreground">6. Security of Your Data</h3>
            <p>
              We use administrative, technical, and physical security measures to help protect your personal
              information (e.g., Supabase utilizes encryption at rest and in transit). While we have taken reasonable
              steps to secure the personal information you provide, please be aware that no security measures are
              perfect or impenetrable.
            </p>
          </section>

          <section className="space-y-2">
            <h3 className="text-base font-semibold text-foreground">7. Children's Privacy</h3>
            <p>
              The App is not intended for children under the age of 13. We do not knowingly collect personally
              identifiable information from children under 13. If we discover that a child under 13 has provided us
              with personal information, we will delete such information from our servers immediately.
            </p>
          </section>

          <section className="space-y-2">
            <h3 className="text-base font-semibold text-foreground">8. Changes to This Privacy Policy</h3>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any changes by updating the
              "Last Updated" date at the top of this Policy and, where appropriate, through an in-app notification.
              You are advised to review this Privacy Policy periodically for any changes.
            </p>
          </section>

          <section className="space-y-2">
            <h3 className="text-base font-semibold text-foreground">9. Contact Us</h3>
            <p>
              If you have any questions or concerns about this Privacy Policy or our data practices, please contact
              us at:
            </p>
            <p>
              Arab Western Ltd<br />
              Email: mediaforkit@gmail.com<br />
              Website: https://forkit.uk
            </p>
          </section>
        </div>
      </SheetContent>
    </Sheet>
  );
};
